import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import marketplaceModule from '../../api/modules/marketplace.module';
import { mediaUrl } from '../../utils/common';
import { TCGSetsView } from './TCGSetsPage.view';

/**
 * The sets inside one game, the container. Both fetches, the catalog-group
 * de-duplication and the search term.
 *
 * Everything visual lives in `TCGSetsPage.view.jsx`, which is pure, so the
 * state explorer can mount the real screen without the API running.
 *
 * BEHAVIOUR PRESERVED EXACTLY
 * ---------------------------
 * Two independent effects, both keyed on `tcgId`. The first resolves the game
 * from the categories list and is the only thing that can produce "not found".
 * The second fetches the catalogs and folds duplicate `catalog_group_id`s into
 * one entry, summing `total_products` and keeping the first image it sees.
 *
 * Sets are LANGUAGE-AGNOSTIC, one `CardSet` per source ref, so the request is
 * never scoped to a language and there is no language filter. `getTCGCategoryCatalogs`
 * is still called with the bare `tcgId`, which is what the test asserts.
 *
 * The stale-error clear on refetch is kept: without it a successful second
 * fetch still renders the previous error instead of the grid.
 */
export default function TCGSetsPage() {
  const { tcgId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [sets, setSets] = useState([]);
  const [tcgCategory, setTcgCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Resolve the game itself.
  useEffect(() => {
    const fetchTCGData = async () => {
      if (!tcgId) return;

      try {
        setError(null);

        const categoriesResponse = await marketplaceModule.getTCGCategories();
        if (categoriesResponse.success) {
          const category = categoriesResponse.data.find((cat) => cat.id === tcgId);
          if (category) {
            setTcgCategory(category);
          } else {
            setError('Trading Card Game category not found');
          }
        }
      } catch (err) {
        setError('Failed to load TCG data');
        console.error('Error fetching TCG data:', err);
      }
    };

    fetchTCGData();
  }, [tcgId]);

  // Fetch the catalogs (sets) for this game.
  useEffect(() => {
    const fetchCatalogs = async () => {
      if (!tcgId) return;

      try {
        setLoading(true);
        // Clear any stale error so a successful refetch actually renders the
        // sets grid instead of the previous error state.
        setError(null);
        const response = await marketplaceModule.getTCGCategoryCatalogs(tcgId);

        if (response.success) {
          // Group by catalog_group_id to avoid duplicates.
          const catalogMap = new Map();

          response.data.forEach((catalog) => {
            const groupId = catalog.catalog_group_id;

            if (!catalogMap.has(groupId)) {
              catalogMap.set(groupId, {
                id: groupId,
                catalogGroupId: groupId,
                name: catalog.catalog_group || 'Unknown Set',
                slug: groupId, // catalog_group_id is the slug
                cardCount: catalog.total_products || 0,
                logo: mediaUrl(catalog.catalog_image_url),
                releaseDate: null,
                series: null,
              });
            } else {
              const existing = catalogMap.get(groupId);
              existing.cardCount = (existing.cardCount || 0) + (catalog.total_products || 0);
              if (!existing.logo && catalog.catalog_image_url) {
                existing.logo = mediaUrl(catalog.catalog_image_url);
              }
            }
          });

          setSets(Array.from(catalogMap.values()));
        } else {
          setError(response.message || 'Failed to load sets');
        }
      } catch (err) {
        setError('An error occurred while loading sets');
        console.error('Error fetching catalogs:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCatalogs();
  }, [tcgId]);

  const filteredSets = useMemo(
    () => sets.filter((set) => set.name.toLowerCase().includes(searchQuery.toLowerCase())),
    [sets, searchQuery],
  );

  // "Not found" is the resolved absence of the game, never the moment before
  // the categories call has answered.
  const notFound = !loading && !tcgCategory;

  return (
    <TCGSetsView
      selfPath={`/sets/category/${tcgId}`}
      gameName={tcgCategory?.name}
      sets={filteredSets}
      loading={loading}
      error={error}
      notFound={notFound}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
    />
  );
}
